import { ActionDoneType } from "@/enums/ActionDoneType.enum";
import { SharingHelper } from "@/helpers/sharing.helper";
import { UserHelper } from "@/helpers/user.helper";
import { Paging } from "@/models/Paging";
import { AlertService } from "@/services/alert.service";
import { AppConfigService } from "@/services/app-config.service";
import { AuthenticationService } from "@/services/authentication.service";
import { AsyncPipe, NgFor, NgIf, formatDate } from "@angular/common";
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatMenuModule } from "@angular/material/menu";
import { MatPaginator, MatPaginatorModule } from "@angular/material/paginator";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import {
  RealProperty,
  Sharing,
  SharingScope,
  SharingService,
} from "@eemo/api-client";
import { Subscription } from "rxjs";
import { ConfirmDialogComponent } from "../confirm-dialog/confirm-dialog.component";
import { InfoDialogComponent } from "../info-dialog/info-dialog.component";
import { ShareDialogComponent } from "../share-dialog/share-dialog.component";
import { ShareStatDialogComponent } from "../share-stat-dialog/share-stat-dialog.component";

import { TooltipDirective } from "@/directives/tooltip/tooltip.directive";
import { ObjectUpdatedType } from "@/enums/ObjectUpdatedType.enum";
import { DateFormatPipe } from "@/extensions/datePipe";
import { ActionService } from "@/services/action.service";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-share-table",
  templateUrl: "./share-table.component.html",
  styleUrls: ["./share-table.component.scss"],
  standalone: true,
  imports: [
    NgIf,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    MatMenuModule,
    MatPaginatorModule,
    NgFor,
    AsyncPipe,
    TooltipDirective,
    DateFormatPipe,
  ],
})
export class ShareTableComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @Input() shares: Sharing[];
  @Input() ownedProperties: RealProperty[];
  @Input() curProp: RealProperty;
  @Input() modeMyShares: boolean;
  @Input() showAddButton = false;
  @Input() showIncreaseViewTooltip = false;
  @Input() showPropertyName = true;
  @Input() canUpdateShares = true;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @Output() sharesUpdated = new EventEmitter<boolean>();

  dataSource: MatTableDataSource<Sharing>;
  displayedColumns: string[];
  displayedColumnsMyShares = [
    "propertyName",
    "targetUserName",
    "dateCreate",
    "scope",
    "allowedData",
    "expirationDate",
    "consulted",
    "actions",
  ];

  displayedColumnsMyAccesses = [
    "propertyName",
    "sharedByUser",
    "dateCreate",
    "scope",
    "allowedData",
    "expirationDate",
    "actions",
  ];

  subs: Subscription[] = [];

  sharingDurations: string[];
  sharingDurationNames: { [key: string]: string } = {};

  paging = new Paging();

  constructor(
    private sharingService: SharingService,
    private sharingHelper: SharingHelper,
    private dialog: MatDialog,
    private alertService: AlertService,
    public authenticationService: AuthenticationService,
    public actionService: ActionService,
    private appConfigService: AppConfigService,
    @Inject(LOCALE_ID) public locale: string
  ) {}

  ngOnInit() {
    this.displayedColumns = this.modeMyShares
      ? this.displayedColumnsMyShares
      : this.displayedColumnsMyAccesses;
    if (!this.showPropertyName) {
      this.displayedColumns = this.displayedColumns.filter(
        (e) => e !== "propertyName"
      );
    }
    this.loadData();
    this.sharingDurations = this.sharingHelper.getSharingDurations();
    this.sharingDurations.forEach(
      (d) =>
        (this.sharingDurationNames[d] =
          this.sharingHelper.getSharingDurationName(d))
    );

    this.subs.push(
      this.appConfigService.objectUpdated.subscribe((type) => {
        if (type === ObjectUpdatedType.SHARE) {
          this.loadData();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.shares && !changes.shares.firstChange) {
      this.loadData();
    }
  }

  loadData() {
    let filter: string;
    if (this.dataSource && this.dataSource.filter) {
      filter = this.dataSource.filter;
    }
    const displayedData = [];
    this.shares.forEach((i) => {
      const row = {
        share: i,
        propertyName: i.property.name,
        propertyOwner: UserHelper.userSummmaryDisplayName(i.propertyOwner),
        targetUserDeleted: i.targetUser?.deleted,
        sharedByOtherUser: i.propertyOwner.uid != i.user?.uid,
        sharedBy: UserHelper.userSummmaryDisplayName(i.user),
        scope: this.sharingHelper.getSharingScopeName(i.scope),
        defaultAllowedData:
          SharingScope.VIEWING === i.scope && i.allowedData?.length === 0,
        fullAllowedData:
          SharingScope.DELEGATION === i.scope ||
          i.allowedData?.length ===
            this.sharingHelper.getSharingDataCategories().length,
        allowedData: this.sharingHelper.getAllowedDataName(
          i.allowedData,
          i.scope
        ),
        allowedDataDetail: this.sharingHelper.getAllowedDataDetailNames(
          i.allowedData,
          i.property.type
        ),
        expirationDate: this.sharingHelper.getExpirationDateName(i.endDate),
        property: i.property.name,
        consulted: i.consulted,
        visitMode: i.visitMode,
        visitAcknowledged: i.visitAcknowledged,
        viewCount: i.viewCount,
        dateCreate: i.dateCreate,
        notDelivered: i.notDelivered,
        targetUserEmail: i.targetUserEmail,
        targetUserName: i.targetUser
          ? UserHelper.userSummmaryDisplayName(i.targetUser)
          : i.targetUserEmail,
        canAskFeedback:
          this.curProp?.onGoingSelling &&
          i.targetUser &&
          !i.hasFeedback &&
          this.canUpdateShares &&
          !i.targetUser?.deleted,
        canAddFeedback:
          this.curProp?.onGoingSelling &&
          i.targetUser &&
          !i.hasFeedback &&
          this.canUpdateShares &&
          !i.targetUser?.deleted,
        canDeleteFeedback: i.hasFeedback && this.canUpdateShares,
        canResendNotification:
          this.canUpdateShares &&
          !this.sharingHelper.isSharingExpired(i.endDate) &&
          !i.targetUser?.deleted,
        hasFeedback: i.hasFeedback,
      };
      displayedData.push(row);
    });
    // sort the data by view count and date create
    displayedData.sort((a, b) => {
      if (a.viewCount < b.viewCount) {
        return 1;
      } else if (a.viewCount > b.viewCount) {
        return -1;
      } else {
        if (a.dateCreate < b.dateCreate) {
          return 1;
        } else if (a.dateCreate > b.dateCreate) {
          return -1;
        } else {
          return 0;
        }
      }
    });
    this.dataSource = new MatTableDataSource(displayedData);
    // *ngIf break the paginator and sort : must be set in background
    setTimeout(() => {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
    });
    this.dataSource.sort = this.sort;
    if (filter) {
      this.applyFilter(filter);
    }
  }

  ngAfterViewInit() {
    // If the user changes the sort order, reset back to the first page.
    if (this.sort) {
      this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    }
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  extendDuration(share: Sharing, duration: string) {
    share.endDate = this.sharingHelper.calculateEndDate(
      duration,
      share.endDate
    );
    this.subs.push(
      this.sharingService.updateSharing(share.uid, share).subscribe(() => {
        this.alertService.success("Modification effectuée avec succès");
        this.sharesUpdated.emit(true);
      })
    );
  }

  edit(shareToEdit: Sharing) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ShareDialogComponent, {
        // width: '20rem'
        data: { share: shareToEdit, edition: true },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.sharesUpdated.emit(true);
        }
      });
    }
  }

  confirmDelete(share: Sharing) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message:
            'Confirmez-vous la suppression du partage pour "' +
            (share.targetUser
              ? UserHelper.userSummmaryDisplayName(share.targetUser)
              : share.targetUserEmail) +
            '" ?',
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDelete(share);
        }
      });
    }
  }

  doDelete(share: Sharing) {
    this.subs.push(
      this.sharingService.deleteSharing(share.uid).subscribe(() => {
        this.alertService.success("Suppression effectuée avec succès");
        this.sharesUpdated.emit(true);
      })
    );
  }

  viewStats(share: Sharing) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      this.dialog.open(ShareStatDialogComponent, {
        data: { share },
      });
    }
  }

  addShare(prop: RealProperty) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ShareDialogComponent, {
        data: { props: [prop] },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.appConfigService.triggerActionDone(ActionDoneType.SHARE);
        }
      });
    }
  }

  showNotDeliveredInfo(share: Sharing) {
    this.dialog.open(InfoDialogComponent, {
      maxWidth: "30rem",
      data: {
        title: "Email non distribué",
        okButtonText: "Fermer",
        content:
          "<div class=\"text-center\"><p>L'email de partage n'a pas pu être distribué.</p><p>Veuillez vérifier l'adresse du destinataire <i>" +
          share.targetUserEmail +
          "</i>.</p></div>",
      },
    });
  }

  showSharedByInfo(share: Sharing) {
    this.dialog.open(InfoDialogComponent, {
      maxWidth: "30rem",
      data: {
        title: "Partage par délégation",
        okButtonText: "Fermer",
        content:
          '<p class="text-center">Partagé par ' +
          UserHelper.userSummmaryDisplayName(share.user) +
          " le " +
          formatDate(share.dateCreate, "yyyy-MM-dd", this.locale) +
          ".</p>",
      },
    });
  }

  confirmAskFeedback(share: Sharing) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message:
            "Confirmez-vous l'envoi du rapport de visite à compléter à \"" +
            UserHelper.userSummmaryDisplayName(share.targetUser) +
            '" ?',
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doAskFeedback(share);
        }
      });
    }
  }

  doAskFeedback(share: Sharing) {
    this.subs.push(
      this.sharingService
        .askForSharingVisitFeedback(share.uid)
        .subscribe(() => {
          this.alertService.success("Envoi effectué avec succès");
        })
    );
  }

  confirmDeleteFeedback(share: Sharing) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message:
            'Confirmez-vous la suppression du rapport de visite de "' +
            UserHelper.userSummmaryDisplayName(share.targetUser) +
            '" ?',
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDeleteFeedback(share);
        }
      });
    }
  }

  doDeleteFeedback(share: Sharing) {
    this.subs.push(
      this.sharingService
        .deleteSharingVisitFeedback(share.uid)
        .subscribe(() => {
          this.alertService.success("Rapport de visite supprimé");
          this.appConfigService.triggerActionDone(ActionDoneType.SHARE);
          this.appConfigService.triggerObjectUpdated(ObjectUpdatedType.SHARE);
        })
    );
  }

  addFeedback(share: Sharing) {
    this.actionService.addVisitFeedback(share.uid);
  }

  viewFeedback(shareUid: string) {
    this.actionService.viewVisitFeedback(shareUid);
  }

  confirmResendNotification(share: Sharing) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message:
            "Confirmez-vous le renvoi d'une notification de partage à \"" +
            UserHelper.userSummmaryDisplayName(share.targetUser) +
            '" ?',
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doResendNotification(share);
        }
      });
    }
  }

  doResendNotification(share: Sharing) {
    this.subs.push(
      this.sharingService.resendSharingNotification(share.uid).subscribe(() => {
        this.alertService.success("Notification envoyée");
      })
    );
  }

  showIncreaseViewDoc() {
    this.actionService.showHelpScoutArticle(
      environment.SHARE_INCREASE_VIEW_ARTICLE_ID
    );
  }
}
